"use client";

import * as React from "react";
import { Check, ChevronDown, Loader2, Plus, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { pickerTriggerClass } from "./trigger-recipes";

/**
 * Job B — FIND (`docs/picker-taxonomy.md` §1.3, §2.3, §3.2, §4.2).
 *
 * Set a property from a long or data-sourced list: a person, a project, a
 * task, a milestone, a permission role, a label — or any fixed list past
 * `PICKER_THRESHOLD` (12). Below that threshold, and for a taxonomy that
 * classifies the record (status, priority, type, visibility, frequency), the
 * mechanism is `Select`, not this.
 *
 * A composition of `Popover` + `Command` + `pickerTriggerClass` — no new
 * machinery. It is exactly what `property-menus.tsx` and
 * `currency-selector.tsx` each hand-assembled, and its trigger is
 * pixel-identical to a `SelectTrigger` of the same `variant`, which is the
 * entire point: two controls that look the same must *be* the same.
 *
 * **The accessibility fix this exists to carry.** The trigger is
 * `aria-haspopup="dialog"`, **not** `role="combobox"` — the combobox is the
 * search input inside the panel, which is the only element that actually owns
 * a list. The old `Combobox` put `role="combobox"` on a button with no
 * `aria-controls`, no `aria-activedescendant` and no `aria-autocomplete`, then
 * made all 200 options tab stops. Here cmdk owns the input's combobox
 * semantics and the roving `aria-activedescendant`; options are never tab
 * stops.
 *
 * **One documented deviation.** §4.2 asks for `aria-selected` on an option to
 * mean *chosen*. cmdk 1.1.1 writes `aria-selected` itself, after spreading
 * caller props, to mean *cursor* (which is the APG combobox convention), and
 * it cannot be overridden without fighting the library on every keystroke. So
 * chosen state is carried by `aria-checked`, which `role="option"` supports in
 * ARIA 1.2 and which screen readers announce as "checked" / "not checked".
 * The visible trailing check and `aria-checked` therefore always agree.
 */

export interface PickerOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Group heading. Options carrying one are grouped without a `groupBy`. */
  group?: string;
  /** Leading dot / avatar / glyph — spaced from the label by `gap-1.5`. */
  icon?: React.ReactNode;
  [key: string]: unknown;
}

export type PickerVariant = "field" | "inline" | "compact";

export interface PickerProps {
  options: PickerOption[];
  value?: string | string[];
  onChange?: (value: string | string[] | undefined) => void;
  /** Toggling stays open and preserves the query (§4.2). */
  multiple?: boolean;
  /** The sibling clear button. Multi-select uses the `Clear all` footer too. */
  clearable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Server-side search. Pair with `filterOption={() => true}` to disable client filtering. */
  onSearch?: (query: string) => void;
  filterOption?: (option: PickerOption, query: string) => boolean;
  groupBy?: (option: PickerOption) => string;
  renderOption?: (option: PickerOption, isSelected: boolean) => React.ReactNode;
  /** Closed-trigger content. Defaults to the matched label(s). */
  /**
   * Custom trigger value. `selected` carries only the options whose labels are
   * currently resolved; `selectedCount` is the true number chosen. They differ
   * on a server-searched picker, so anything that renders a count must use the
   * second argument, not `selected.length`.
   */
  renderValue?: (
    selected: PickerOption[],
    selectedCount: number,
  ) => React.ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  maxHeight?: number;
  variant?: PickerVariant;
  /**
   * The trigger never shows a value and reads as a verb — "Link a task",
   * "Add role" (§1.3). Still Job B, not Job C: choosing an entry sets a value
   * on the record, the phrasing is the only difference.
   */
  action?: boolean;
  chevron?: boolean;
  /**
   * Leading glyph inside the trigger, before the value or the placeholder.
   *
   * For a chip that has dropped its caret (`chevron={false}`): with no caret
   * and no fill, a trigger whose value is a bare word is indistinguishable from
   * a label, and the glyph is what says "this is a control" — and, once several
   * chips sit in a row, which control it is.
   *
   * Rendered unconditionally, so a call site that wants it only while the
   * picker is empty passes it conditionally. That is the common case for a
   * picker whose *value* carries its own glyph (a status dot, an avatar
   * stack), where an unconditional icon would render two marks for one field.
   */
  icon?: React.ReactNode;
  /**
   * Controlled open state, mirroring `Select`'s. The inline click-to-edit
   * editors (§4.4) render a picker already open and close it on commit or
   * dismissal; without this the same row could be a forced-open `Select` below
   * the twelve-option threshold and an un-openable `Picker` above it.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "end";
  contentWidth?: "trigger" | "auto" | number;
  className?: string;
  popoverClassName?: string;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

const defaultFilterOption = (option: PickerOption, query: string): boolean =>
  option.label.toLowerCase().includes(query.toLowerCase());

const Picker = React.forwardRef<HTMLButtonElement, PickerProps>(function Picker(
  {
    options,
    value,
    onChange,
    multiple = false,
    clearable = true,
    disabled = false,
    loading = false,
    onSearch,
    filterOption = defaultFilterOption,
    groupBy,
    renderOption,
    renderValue,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    emptyMessage = "No results",
    maxHeight = 320,
    variant = "field",
    action = false,
    chevron,
    icon,
    open: openProp,
    onOpenChange,
    align = "start",
    contentWidth,
    className,
    popoverClassName,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
  },
  ref,
) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  // Uncontrolled by default; `open` hands the state to the caller without
  // changing a single existing call site.
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const showChevron = chevron ?? !action;

  const selectedValues = React.useMemo<string[]>(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  /**
   * cmdk trims an item's `value`, so a value with incidental whitespace has
   * to resolve under both spellings or its row would filter itself out.
   */
  const optionByValue = React.useMemo(() => {
    const map = new Map<string, PickerOption>();
    for (const option of options) {
      map.set(option.value, option);
      const trimmed = option.value.trim();
      if (trimmed !== option.value) map.set(trimmed, option);
    }
    return map;
  }, [options]);

  const selectedOptions = React.useMemo(
    () =>
      selectedValues
        .map((v) => optionByValue.get(v))
        .filter((o): o is PickerOption => Boolean(o)),
    [selectedValues, optionByValue],
  );

  /**
   * Hand cmdk the caller's predicate rather than filtering the array here:
   * cmdk's own filter pass is what hides a `CommandGroup` whose every child
   * is filtered out, and what keeps `CommandEmpty` honest. The old
   * `Combobox` filtered its own array and left group headings standing over
   * nothing.
   */
  const cmdkFilter = React.useCallback(
    (itemValue: string, search: string): number => {
      if (!search) return 1;
      const option = optionByValue.get(itemValue);
      if (!option) {
        return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
      }
      return filterOption(option, search) ? 1 : 0;
    },
    [optionByValue, filterOption],
  );

  const handleQueryChange = React.useCallback(
    (next: string) => {
      setQuery(next);
      onSearch?.(next);
    },
    [onSearch],
  );

  /**
   * Closing empties the search box, so a caller doing server-side search has
   * to be told, or the next open would show an empty box over a still-
   * filtered list. Guarded on a non-empty query, so a picker the user only
   * clicked through issues no request at all.
   */
  const resetQuery = React.useCallback(() => {
    if (query) onSearch?.("");
    setQuery("");
  }, [onSearch, query]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) resetQuery();
    },
    [resetQuery],
  );

  const handleSelect = React.useCallback(
    (optionValue: string) => {
      if (multiple) {
        const next = selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        // Stay open, keep the query — the point of multi-select is picking
        // several things out of one search (§4.2).
        onChange?.(next.length > 0 ? next : undefined);
        return;
      }
      // Never `optionValue === value ? undefined : optionValue`. A listbox
      // must not lose its value when the user confirms it; clearing is the
      // clear button's job.
      onChange?.(optionValue);
      // Closed by us, not by Radix, so Radix's `onOpenChange` never fires
      // for it — `setOpen` notifies a controlled caller directly.
      setOpen(false);
      resetQuery();
    },
    [multiple, onChange, resetQuery, selectedValues],
  );

  const handleClear = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onChange?.(undefined);
    },
    [onChange],
  );

  const handleTriggerKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "ArrowDown" && !open) {
        event.preventDefault();
        setOpen(true);
      }
    },
    [open],
  );

  /* ── Trigger ────────────────────────────────────────────────────────── */

  const showClear =
    clearable && !action && !disabled && selectedValues.length > 0;

  /*
      Counts come from `selectedValues`, labels from `selectedOptions`. The two
      differ whenever a chosen value is not in the current option page — which
      is the normal state of a server-searched picker — and the count must not
      silently shrink because a label has not been fetched yet. A single
      unresolvable value falls back to the placeholder, as it always has.
    */
  let valueNode: React.ReactNode = null;
  if (selectedValues.length > 0) {
    if (renderValue) {
      valueNode = renderValue(selectedOptions, selectedValues.length);
    } else if (selectedValues.length === 1) {
      const only = selectedOptions[0];
      valueNode = only ? (
        <>
          {only.icon}
          <span className="truncate">{only.label}</span>
        </>
      ) : null;
    } else {
      valueNode = (
        <span className="truncate">{selectedValues.length} selected</span>
      );
    }
  }
  // Every text branch is wrapped in its own `truncate` span: the value slot
  // is a flex row, and `text-overflow` does not reach an anonymous text box
  // inside one — an unwrapped string would hard-clip instead of eliding.
  const hasValue = valueNode !== null;
  if (!hasValue) {
    valueNode = <span className="truncate">{placeholder}</span>;
  }

  const caretSize = variant === "compact" ? "size-3" : "size-3.5";
  const clearName = ariaLabel ?? placeholder;
  const widthClass = contentWidthClass(variant, contentWidth);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {/*
          The clear affordance is a **sibling** of the trigger, never a nested
          interactive element inside it: a `<button>` (or a `role="button"`
          span) inside a `<button>` is invalid, unreachable by keyboard in the
          order the user expects, and ambiguous to every screen reader.
        */}
      <span
        className={cn(
          "relative inline-flex items-center",
          variant === "compact" ? "max-w-full shrink-0" : "w-full",
        )}
      >
        <PopoverTrigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            data-slot="picker-trigger"
            data-placeholder={!action && !hasValue ? "" : undefined}
            onKeyDown={handleTriggerKeyDown}
            className={cn(
              pickerTriggerClass(variant),
              showClear &&
                (showChevron
                  ? variant === "field"
                    ? "pr-11"
                    : "pr-9"
                  : "pr-7"),
              className,
            )}
          >
            {action ? (
              /* One element, so the recipe's `justify-between` cannot push
                   the verb away from its `+`. */
              <span className="flex min-w-0 items-center gap-1.5 text-text-secondary">
                <Plus className="size-3.5" aria-hidden />
                <span className="truncate">{placeholder}</span>
              </span>
            ) : (
              <span
                className={cn(
                  "flex min-w-0 items-center gap-1.5 truncate",
                  !hasValue && "text-text-tertiary",
                )}
              >
                {icon}
                {valueNode}
              </span>
            )}
            {showChevron ? (
              <ChevronDown
                aria-hidden
                className={cn(caretSize, "text-text-tertiary")}
              />
            ) : null}
          </button>
        </PopoverTrigger>
        {showClear ? (
          <button
            type="button"
            onClick={handleClear}
            aria-label={`Clear ${clearName}`}
            className={cn(
              "absolute grid size-4 place-items-center rounded-xs",
              // Clears the caret on its left and stops inside the trigger's
              // reserved right padding on its right: `field` reserves 44px
              // for a 14px caret, the denser variants 36px for a 12px one.
              showChevron
                ? variant === "field"
                  ? "right-6"
                  : "right-5"
                : "right-1.5",
              "text-text-tertiary hover:bg-hover hover:text-text",
              "transition-[background-color,color] duration-100 ease-out-fast",
              "focus-visible:outline-none focus-fill",
            )}
          >
            <X className="size-3" aria-hidden />
          </button>
        ) : null}
      </span>

      {/* ── Panel ─────────────────────────────────────────────────────── */}
      <PopoverContent
        align={align}
        sideOffset={4}
        className={cn("overflow-hidden p-0", widthClass, popoverClassName)}
        style={
          typeof contentWidth === "number" ? { width: contentWidth } : undefined
        }
      >
        <Command
          label={ariaLabel ?? searchPlaceholder}
          loop
          filter={cmdkFilter}
        >
          <CommandInput
            size="sm"
            value={query}
            onValueChange={handleQueryChange}
            placeholder={searchPlaceholder}
            trailing={
              loading ? (
                <Loader2
                  className="size-3.5 shrink-0 animate-spin text-text-tertiary"
                  aria-hidden
                />
              ) : null
            }
          />
          <CommandList
            label={ariaLabel ?? searchPlaceholder}
            aria-multiselectable={multiple || undefined}
            style={{ maxHeight }}
            onWheel={(event) => event.stopPropagation()}
          >
            <CommandEmpty>{loading ? "Loading…" : emptyMessage}</CommandEmpty>
            <PickerOptions
              options={options}
              groupBy={groupBy}
              selectedValues={selectedValues}
              renderOption={renderOption}
              onSelect={handleSelect}
            />
          </CommandList>
          {multiple && selectedValues.length > 0 ? (
            /* Footer inside an overlay panel: separated by space, not a rule.
                 A `bg-panel` bar here would nest a raised surface inside the
                 overlay tier (§2), so the existing `p-1` does the work. */
            <div className="p-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => onChange?.(undefined)}
              >
                Clear all ({selectedValues.length})
              </Button>
            </div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
});
Picker.displayName = "Picker";

/**
 * `field` matches the trigger and grows no narrower than 224px; `inline` and
 * `compact` hang off triggers as small as 24px, so they take a fixed 256px
 * rather than collapsing to the width of a chip (§3.2).
 */
function contentWidthClass(
  variant: PickerVariant,
  contentWidth: PickerProps["contentWidth"],
): string | undefined {
  if (typeof contentWidth === "number") return undefined;
  const resolved = contentWidth ?? (variant === "field" ? "trigger" : "auto");
  return resolved === "trigger"
    ? "w-(--radix-popover-trigger-width) min-w-56"
    : "w-64";
}

interface PickerOptionsProps {
  options: PickerOption[];
  groupBy?: (option: PickerOption) => string;
  selectedValues: string[];
  renderOption?: (option: PickerOption, isSelected: boolean) => React.ReactNode;
  onSelect: (value: string) => void;
}

function PickerOptions({
  options,
  groupBy,
  selectedValues,
  renderOption,
  onSelect,
}: PickerOptionsProps) {
  const renderItem = (option: PickerOption) => {
    const isSelected = selectedValues.includes(option.value);
    return (
      <CommandItem
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        marker={false}
        aria-checked={isSelected}
        onSelect={() => onSelect(option.value)}
      >
        {/*
          The icon→label `gap-1.5` — the mirror of `SelectItem`'s `ItemText`
          rule, so a dot or an avatar is spaced from its label identically in
          both mechanisms. Without it a `StatusDot` + label renders "•Label".
        */}
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          {renderOption ? (
            renderOption(option, isSelected)
          ) : (
            <>
              {option.icon}
              <span className="truncate">{option.label}</span>
            </>
          )}
        </span>
        {/*
          Always rendered, opacity-toggled, so a row never shifts as the
          cursor moves. Right-hand, matching `SelectItem`, where it stays
          clear of a leading dot or avatar.
        */}
        <Check
          aria-hidden
          className={cn(
            "ml-auto size-3.5 shrink-0 text-accent-text",
            isSelected ? "opacity-100" : "opacity-0",
          )}
        />
      </CommandItem>
    );
  };

  const groups = React.useMemo(() => {
    const resolve =
      groupBy ??
      (options.some((o) => typeof o.group === "string" && o.group)
        ? (option: PickerOption) => option.group ?? ""
        : undefined);
    if (!resolve) return null;

    const map = new Map<string, PickerOption[]>();
    for (const option of options) {
      const key = resolve(option) || "";
      const bucket = map.get(key);
      if (bucket) bucket.push(option);
      else map.set(key, [option]);
    }
    return Array.from(map.entries());
  }, [options, groupBy]);

  if (!groups) return <>{options.map(renderItem)}</>;

  return (
    <>
      {groups.map(([heading, groupOptions]) => (
        <CommandGroup
          key={heading || "\0ungrouped"}
          heading={heading || undefined}
        >
          {groupOptions.map(renderItem)}
        </CommandGroup>
      ))}
    </>
  );
}

export { Picker };
